/*
  ss-market-route.js — every visitor lands in the store for where they are.

  Shopify moves some visitors between domains itself, but a German, Dutch or
  Czech shopper who opened a sacrasoul.pl link stayed in the Polish store, in
  Polish and złoty (real browsers in DE, NL, CZ, 28 Sep 2026). Sho: move them,
  accurately, and make the language/currency control easy to find.

  On the first page of a session this asks Shopify's own
  /browsing_context_suggestions.json where the visitor is. When that country is
  sold by a DIFFERENT market (Liquid country.market.handle, so sacrasoul.nl is
  told apart from the EU store though both price in EUR), it posts Shopify's
  localization form: Shopify moves domain and currency and returns them to the
  same page. The browser's language goes along when that store publishes it;
  otherwise Shopify uses the store's default. A cart and a discount code do NOT
  cross domains (real browser from DE, 29 Sep 2026: 1 item + SACRA10 on .pl
  arrived as an empty .com cart), so anyone holding either stays put.

  Never moved: search crawlers and other bots, prerenders, iframes, the theme
  editor and previews, checkout/cart/account pages, and anyone who has ever
  picked a region or language in our switchers (both write ss_mkt_chosen or
  sacrasoul_locale_set), and anyone with items in the cart or a discount code
  applied here. Once per tab session per site, and the landing URL carries
  ss_rt=1 so the destination never routes again (no loop across domains).

  After an automatic move, a small note says where they are, in their language,
  with a button that opens the region picker; "×" or 12 s closes it. Every word
  is already-approved theme copy (announcement ship-to phrase, the picker's own
  labels) in en/pl/de/nl/cs. Proved by tests/dom/market-route.test.cjs.
*/
(function () {
  'use strict';

  var COPY = {
    en: { ships: 'Ships to', change: 'Select region and currency', aria: 'Region and currency settings', close: 'Close' },
    pl: { ships: 'Kraj dostawy:', change: 'Wybierz region i walutę', aria: 'Ustawienia regionu i waluty', close: 'Zamknij' },
    de: { ships: 'Versand nach', change: 'Region und Währung auswählen', aria: 'Regions- und Währungseinstellungen', close: 'Schließen' },
    nl: { ships: 'Verzending naar:', change: 'Regio en valuta selecteren', aria: 'Instellingen voor regio en valuta', close: 'Sluiten' },
    cs: { ships: 'Země doručení:', change: 'Vybrat region a měnu', aria: 'Nastavení regionu a měny', close: 'Zavřít' }
  };
  var ROUTED = 'ss_mkt_routed';
  var MARK = 'ss_rt';
  var PICK = 'ss_pick';
  var CHOSEN = 'ss_mkt_chosen=1; path=/; max-age=31536000; samesite=lax';
  var BOT = /bot|crawl|spider|slurp|headless|lighthouse|facebookexternalhit|embedly|preview|google-inspectiontool|bingpreview|chrome-lighthouse|pagespeed|gtmetrix|ahrefs|semrush/i;
  var NO_ROUTE_PATH = /\/(checkouts?|cart|account|challenge|password|policies)(\/|$)/;

  function cookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; *)' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }

  function primaryLangs(list) {
    var out = [];
    for (var i = 0; i < (list || []).length; i++) {
      var t = String(list[i] || '').toLowerCase().split('-')[0];
      if (t && out.indexOf(t) === -1) out.push(t);
    }
    return out;
  }

  /* The language a market should serve a visitor it is moving in: the first of
     their browser languages it publishes; else one they read (READS, one way);
     else English where published; else the market default. pub lists the
     market's languages, default first. */
  var NEAR = { pl: 'uk ru be cs sk', cs: 'sk pl', nl: 'af fy', de: 'lb' };
  function isNear(lang, of) { return (' ' + (NEAR[of] || '') + ' ').indexOf(' ' + lang + ' ') !== -1; }
  /* Choosing a language needs a ONE-WAY table: a Slovak reads Czech, but a Pole
     would rather read English than Czech, although Czech pages stay fine for a
     Pole already reading them (NEAR, the stay-put check). */
  var READS = { sk: 'cs', uk: 'pl', ru: 'pl', be: 'pl', af: 'nl', fy: 'nl', lb: 'de' };
  /* Ukrainian, Russian and Belarusian read Polish only IN Poland, where living there
     is the evidence: Polish is published in every market since 2026-10-06, and a
     Ukrainian in Germany or the UK reads English, not Polish. */
  function neighbour(lang, pub, market) {
    if (READS[lang] === 'pl' && market !== 'poland') return '';
    return READS[lang] && pub.indexOf(READS[lang]) !== -1 ? READS[lang] : '';
  }
  function pickLanguage(wanted, pub, market) {
    for (var i = 0; i < wanted.length; i++) { if (pub.indexOf(wanted[i]) !== -1) return wanted[i]; }
    var near = wanted[0] ? neighbour(wanted[0], pub, market) : '';
    if (near) return near;
    return pub.indexOf('en') !== -1 ? 'en' : pub[0];
  }

  /* Pure: route or not, where, and in which language. */
  function decide(ctx) {
    if (ctx.chosen) return null;
    var sug = ctx.response && ctx.response.suggestions && ctx.response.suggestions[0];
    var country = sug && sug.parts && sug.parts.country;
    var cc = country && country.handle;
    if (!cc || cc === ctx.currentCountry) return null;
    if (typeof country.confidence === 'number' && country.confidence < 0.8) return null;
    var target = ctx.markets && ctx.markets[cc];
    if (!target || target === ctx.currentMarket) return null;
    var langs = primaryLangs(ctx.navLangs);
    var pub = ctx.langs && ctx.langs[target] ? String(ctx.langs[target]).split(',').filter(Boolean) : [];
    var lang = '';
    if (pub.length) {
      lang = pickLanguage(langs, pub, target);
    } else {
      /* No language map (older page): hand Shopify a language we have copy for. */
      var sl = sug.parts.language && sug.parts.language.handle;
      if (sl) langs.push(String(sl).toLowerCase());
      for (var i = 0; i < langs.length; i++) { if (COPY[langs[i]]) { lang = langs[i]; break; } }
    }
    return { country: cc, market: target, languageCode: lang };
  }

  function track(event, props, leaving) {
    try {
      if (window.posthog && window.posthog.capture) {
        window.posthog.capture(event, props, leaving ? { transport: 'sendBeacon', send_instantly: true } : undefined);
      }
    } catch (e) { /* analytics never blocks */ }
  }

  function returnTo() {
    var q = location.search.replace(/^\?/, '');
    q = q ? q + '&' + MARK + '=1' : MARK + '=1';
    return location.pathname + '?' + q + location.hash;
  }

  function route(action, choice) {
    var form = document.createElement('form');
    form.id = 'ss-mkt-form';
    form.method = 'post';
    form.action = action;
    var fields = { form_type: 'localization', utf8: '✓', _method: 'put', country_code: choice.country, return_to: returnTo() };
    if (choice.languageCode) fields.language_code = choice.languageCode;
    for (var k in fields) {
      var input = document.createElement('input');
      input.type = 'hidden'; input.name = k; input.value = fields[k];
      form.appendChild(input);
    }
    (document.body || document.documentElement).appendChild(form);
    form.submit();
  }

  /* ---- after an automatic move: a small note with the way to change it ---- */

  /* The note never shares the screen with Shopify's cookie banner. The banner
     can arrive after this runs, so ask the privacy API (the same readiness door
     ss-identity and ss-klaviyo use): shouldShowBanner() === true means the
     visitor has not answered yet, so wait for their answer (or 4 s with no
     banner on screen). Without the API
     (blocked by an extension), watch the page for a banner for a few seconds. */
  function afterCookieChoice(show) {
    var done = false;
    function go() { if (!done) { done = true; setTimeout(show, 400); } }
    function bannerUp() {
      var el = document.getElementById('shopify-pc__banner');
      if (!el) return false;
      var r = el.getBoundingClientRect();
      var cs = getComputedStyle(el);
      return r.width > 0 && r.height > 0 && cs.display !== 'none' && cs.visibility !== 'hidden';
    }
    function waitForAnswer() {
      document.addEventListener('visitorConsentCollected', go, { once: true });
      var tries = 0;
      var timer = setInterval(function () {
        tries++;
        if (done || tries > 120) { clearInterval(timer); return; }
        if (tries > 3 && !bannerUp()) { clearInterval(timer); go(); }
      }, 1000);
    }
    function watchPage() {
      var tries = 0;
      var timer = setInterval(function () {
        if (bannerUp()) { clearInterval(timer); waitForAnswer(); return; }
        if (++tries >= 12) { clearInterval(timer); go(); }
      }, 250);
    }
    var pr = window.SacrasoulPrivacyReady;
    if (!pr || !pr.ready) { watchPage(); return; }
    pr.ready.then(function (api) {
      var pending = false;
      try {
        /* Shopify can show the banner where shouldShowBanner() says it need not
           (seen from Oman, 29 Sep 2026), so an unanswered consent also waits. */
        var c = api && typeof api.currentVisitorConsent === 'function' ? api.currentVisitorConsent() : null;
        pending = !!api && ((typeof api.shouldShowBanner === 'function' && api.shouldShowBanner() === true) || (!!c && c.analytics === ''));
      } catch (e) { /* treat as unknown */ }
      if (pending || bannerUp()) waitForAnswer(); else if (api) go(); else watchPage();
    });
  }

  function landingNote() {
    var lang = (document.documentElement.lang || 'en').toLowerCase().split('-')[0];
    var t = COPY[lang] || COPY.en;
    var cc = window.Shopify && window.Shopify.country;
    var cur = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    var name = cc;
    try { name = new Intl.DisplayNames([lang], { type: 'region' }).of(cc) || cc; } catch (e) { /* keep the code */ }

    var style = document.createElement('style');
    style.textContent =
      '.ss-mkt{position:fixed;left:50%;transform:translate(-50%,var(--ss-mkt-lift,0px));bottom:calc(12px + env(safe-area-inset-bottom));z-index:60;' +
      'width:min(560px,calc(100vw - 24px));display:flex;align-items:center;gap:10px;padding:10px 6px 10px 16px;' +
      'background:#FDFBF9;color:var(--color-ink,#2b211a);border:1px solid rgba(43,33,26,.12);border-radius:14px;' +
      'box-shadow:0 10px 30px rgba(43,33,26,.16);font:inherit;font-size:14px;line-height:1.35}' +
      '.ss-mkt__txt{flex:1;min-width:0;margin:0}.ss-mkt__txt b{font-weight:600}' +
      '.ss-mkt__go{flex:none;min-height:44px;padding:0 14px;border:1px solid var(--color-rust,#B15639);border-radius:999px;background:transparent;color:var(--color-rust,#B15639);font:inherit;font-size:13px;font-weight:600;cursor:pointer}' +
      '.ss-mkt__x{flex:none;width:44px;height:44px;border:0;background:none;color:inherit;font-size:22px;line-height:1;cursor:pointer;opacity:.6}' +
      '.ss-mkt__go:focus-visible,.ss-mkt__x:focus-visible{outline:2px solid var(--color-rust,#B15639);outline-offset:2px}' +
      /* Phones: the sentence and the close on one row, the button full width below. */
      '@media (max-width:559px){.ss-mkt{flex-wrap:wrap;row-gap:4px;padding:8px 4px 10px 14px}.ss-mkt__go{order:3;flex:1 1 100%;margin-right:10px}}' +
      '@media (prefers-reduced-motion:no-preference){.ss-mkt{animation:ss-mkt-in .28s ease-out}@keyframes ss-mkt-in{from{opacity:0;transform:translate(-50%,calc(var(--ss-mkt-lift,0px) + 8px))}}}';
    document.head.appendChild(style);

    var bar = document.createElement('div');
    bar.className = 'ss-mkt';
    bar.setAttribute('role', 'status');
    bar.setAttribute('aria-label', t.aria);
    var txt = document.createElement('p');
    txt.className = 'ss-mkt__txt';
    var go = document.createElement('button');
    go.type = 'button'; go.className = 'ss-mkt__go'; go.textContent = t.change;
    var x = document.createElement('button');
    x.type = 'button'; x.className = 'ss-mkt__x'; x.setAttribute('aria-label', t.close); x.textContent = '×';
    bar.appendChild(txt); bar.appendChild(go); bar.appendChild(x);

    /* Never cover a sticky add-to-cart: sit above whatever owns the bottom edge. */
    function bottomOwnerTop() {
      var top = innerHeight;
      var xs = [innerWidth * 0.25, innerWidth * 0.5, innerWidth * 0.75];
      for (var i = 0; i < xs.length; i++) {
        var stack = document.elementsFromPoint ? document.elementsFromPoint(xs[i], innerHeight - 4) : [];
        for (var j = 0; j < stack.length; j++) {
          if (bar.contains(stack[j])) continue;
          for (var n = stack[j], d = 0; n && n !== document.body && d < 8; n = n.parentElement, d++) {
            var pos = getComputedStyle(n).position;
            if (pos === 'fixed' || pos === 'sticky') {
              var r = n.getBoundingClientRect();
              if (r.bottom >= innerHeight - 2 && r.height < innerHeight / 2 && r.top < top) top = r.top;
              break;
            }
          }
        }
      }
      return top;
    }
    var pending = false;
    function lift() {
      pending = false;
      if (!bar.isConnected) return;
      var gap = innerHeight - bottomOwnerTop();
      bar.style.setProperty('--ss-mkt-lift', gap > 0 ? (-gap) + 'px' : '0px');
    }
    function schedule() { if (!pending) { pending = true; setTimeout(lift, 300); } }
    var timer = null;
    function hide() {
      clearTimeout(timer);
      if (!bar.isConnected) return;
      bar.remove();
      removeEventListener('scroll', schedule);
      removeEventListener('resize', schedule);
      document.removeEventListener('keydown', onKey);
    }
    function onKey(e) { if (e.key === 'Escape' && bar.contains(document.activeElement)) hide(); }
    go.addEventListener('click', function () {
      hide();
      var trigger = document.querySelector('[data-locale-trigger]');
      if (trigger) trigger.click();
      track('ss_market_route_change_opened', { country: cc });
    });
    x.addEventListener('click', hide);
    /* No timeout while someone is reading or about to press the button. */
    function hold() { clearTimeout(timer); }
    function release() {
      if (!bar.isConnected || bar.contains(document.activeElement) || bar.matches(':hover')) return;
      clearTimeout(timer); timer = setTimeout(hide, 6000);
    }
    bar.addEventListener('mouseenter', hold);
    bar.addEventListener('focusin', hold);
    bar.addEventListener('mouseleave', release);
    bar.addEventListener('focusout', function () { setTimeout(release, 0); });
    addEventListener('scroll', schedule, { passive: true });
    addEventListener('resize', schedule, { passive: true });
    document.addEventListener('keydown', onKey);
    document.body.appendChild(bar);
    lift();
    /* Text after insertion, so a screen reader hears the status once. */
    setTimeout(function () {
      txt.appendChild(document.createTextNode(t.ships + ' '));
      var b = document.createElement('b'); b.textContent = name; txt.appendChild(b);
      if (cur) txt.appendChild(document.createTextNode(' · ' + cur));
    }, 50);
    timer = setTimeout(hide, 12000);
  }

  function stripMarkers() {
    try {
      var u = new URL(location.href);
      u.searchParams.delete(MARK);
      u.searchParams.delete(PICK);
      u.searchParams.delete('ss_lang');
      history.replaceState(history.state, '', u.pathname + (u.search || '') + u.hash);
    } catch (e) { /* old browsers keep the marker, harmlessly */ }
  }

  function landed() {
    stripMarkers();
    try { sessionStorage.setItem(ROUTED, '1'); } catch (e) { /* private mode */ }
    track('ss_market_route_landed', { country: window.Shopify && window.Shopify.country });
    var show = function () { afterCookieChoice(landingNote); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', show, { once: true }); else show();
  }

  /* The English fallback for a visitor who stays in this store: none of their
     languages is published here. It lives here, not in the head script, because
     only this script knows whether they are about to move to another store
     (which lands them in the right language in the same step). A manual choice,
     a Meta-ad session hold, or a move in the last 15 seconds (the loop guard; all
     from snippets/locale-auto-detect) stands, and a near neighbour keeps the
     served language (pickLanguage). Otherwise it is decided on every page, not
     once a session (2026-10-06). */
  function justMoved() {
    var t = +String(cookie('sacrasoul_lang_auto')).split('.')[1] || 0;
    return Date.now() / 1000 - t < 15;
  }
  function sameStoreLanguage(tag) {
    var manual = cookie('sacrasoul_locale_set');
    if ((manual && manual !== '1') || cookie('sacrasoul_lang_hold') || justMoved()) return '';
    var map = {};
    try { map = JSON.parse(tag.getAttribute('data-langs') || '{}'); } catch (e) { return ''; }
    var pub = String(map[tag.getAttribute('data-market')] || '').split(',').filter(Boolean);
    if (!pub.length) return '';
    var wanted = primaryLangs(navigator.languages || [navigator.language]);
    var served = String(window.Shopify.locale || '').toLowerCase().split('-')[0];
    /* In place, only ever to a language they read: an exact published match,
       else a near neighbour (Slovak -> Czech), else English. Never away from an
       English page or from a near neighbour they are already reading
       (Ukrainian on sacrasoul.pl/en stays; Slovak on /cs-eu stays). */
    var want = '';
    for (var i = 0; i < wanted.length; i++) { if (pub.indexOf(wanted[i]) !== -1) { want = wanted[i]; break; } }
    if (!want && wanted[0] && served !== 'en' && !isNear(wanted[0], served)) {
      want = neighbour(wanted[0], pub, tag.getAttribute('data-market')) || (pub.indexOf('en') !== -1 ? 'en' : '');
    }
    return want && want !== served ? want : '';
  }

  function switchLanguage(tag, lang) {
    document.cookie = 'sacrasoul_lang_auto=' + lang + '.' + Math.floor(Date.now() / 1000) + '; path=/';
    track('ss_language_fallback', { lang: lang, country: window.Shopify.country }, true);
    var form = document.createElement('form');
    form.method = 'post'; form.action = tag.getAttribute('data-action');
    var fields = { form_type: 'localization', utf8: '✓', _method: 'put', country_code: window.Shopify.country, language_code: lang, return_to: location.pathname + location.search + location.hash };
    for (var k in fields) {
      var input = document.createElement('input');
      input.type = 'hidden'; input.name = k; input.value = fields[k];
      form.appendChild(input);
    }
    (document.body || document.documentElement).appendChild(form);
    form.submit();
  }

  function run() {
    /* A prerendered page (the Google result about to be tapped, the address bar's
       guess) routes when it is shown, not never (2026-10-06). */
    if (document.prerendering) { document.addEventListener('prerenderingchange', run, { once: true }); return; }
    var tag = document.getElementById('ss-market-route');
    if (!tag) return;
    if (new RegExp('[?&]' + PICK + '=1').test(location.search)) { document.cookie = CHOSEN; stripMarkers(); return; }
    if (new RegExp('[?&]' + MARK + '=1').test(location.search)) { landed(); return; }
    if (!window.Shopify || !window.Shopify.country) return;
    /* The language auto-detect (snippets/locale-auto-detect) is already replacing
       this page with another language of the same store. Stand down without
       using this session's turn: the next page routes to the right market. */
    if (window.__ssLocaleRedirecting) return;
    /* A Meta ad arrival on sacrasoul.pl (the head script set __ssAd, 2026-10-03):
       they stay in the store and language the ad linked to, wherever they are
       and whatever their phone's language. Spend this tab's turn so no later page
       of the visit moves them; the head script's session hold (sacrasoul_lang_hold)
       already stops the in-store English fallback (sameStoreLanguage). */
    if (window.__ssAd) {
      try { sessionStorage.setItem(ROUTED, '1'); } catch (e) { /* private mode: turnUsed reads as spent anyway */ }
      return;
    }
    if (window.top !== window.self || window.Shopify.designMode) return;
    if (/[?&](preview_theme_id|_ab|_fd|_sc)=/.test(location.search)) return;
    if (BOT.test(navigator.userAgent || '') || navigator.webdriver && !cookie('ss_internal')) return;
    if (NO_ROUTE_PATH.test(location.pathname)) return;
    var lang = sameStoreLanguage(tag);
    var chosen = cookie('ss_mkt_chosen') || ((cookie('sacrasoul_locale_set') || '1') !== '1');
    /* Cautious by design: a discount cookie left from an earlier visit also
       keeps them here, for that cookie's life. */
    var turnUsed = true;
    try { turnUsed = !!sessionStorage.getItem(ROUTED); } catch (e) { /* private mode: no market move */ }
    if (chosen || cookie('discount_code') || turnUsed) { if (lang) switchLanguage(tag, lang); return; }
    try { sessionStorage.setItem(ROUTED, '1'); } catch (e) { return; }

    var markets = {};
    try { markets = JSON.parse(tag.getAttribute('data-markets') || '{}'); } catch (e) { return; }
    var root = tag.getAttribute('data-root') || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    var url = root + 'browsing_context_suggestions.json?country%5Benabled%5D=true&country%5Bexclude%5D=' +
      encodeURIComponent(window.Shopify.country) + '&language%5Benabled%5D=true&language%5Bexclude%5D=' +
      encodeURIComponent(window.Shopify.locale || '');
    /* The cart cookie outlives an emptied cart, so only a cart with items keeps
       them here; the check runs beside the lookup, and only when a cart exists. */
    var hasItems = cookie('cart')
      ? fetch(root + 'cart.js', { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) { return !c || c.item_count > 0; }, function () { return true; })
      : Promise.resolve(false);
    /* The head script (snippets/locale-auto-detect) read the visitor's country from
       Shopify's Server-Timing header before paint; use it and skip the lookup.
       Without it (older Safari, or that script stood down), ask Shopify. */
    var geo = typeof window.__ssGeo === 'string' && /^[A-Z]{2}$/.test(window.__ssGeo) ? window.__ssGeo : '';
    var lookup = geo
      ? Promise.resolve(geo === window.Shopify.country ? { suggestions: [] } : { suggestions: [{ parts: { country: { handle: geo, confidence: 1 } } }] })
      : fetch(url, { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.json() : null; });
    Promise.all([lookup, hasItems]).then(function (res) {
      if (res[1]) { if (lang) switchLanguage(tag, lang); return; }
      var response = res[0];
      var langMap = {};
      try { langMap = JSON.parse(tag.getAttribute('data-langs') || '{}'); } catch (e) { /* older page: no map */ }
      var choice = decide({ response: response, currentCountry: window.Shopify.country, currentMarket: tag.getAttribute('data-market'), langs: langMap,
        markets: markets, chosen: false, navLangs: navigator.languages || [navigator.language] });
      if (!choice) { if (lang) switchLanguage(tag, lang); return; }
      track('ss_market_routed', { from: window.Shopify.country, to: choice.country, lang: choice.languageCode }, true);
      route(tag.getAttribute('data-action'), choice);
    }).catch(function () { if (lang) switchLanguage(tag, lang); });
  }

  /* A region or language picked by hand is final, on BOTH sides of the move.
     Cookies are per domain, so the choice made on sacrasoul.com must also reach
     sacrasoul.pl: the picker's return address carries ss_pick=1, and the page it
     lands on records the choice there and never routes. Our pickers call this
     before form.submit() (which fires no submit event); the listener covers any
     plain submit button posting to /localization. */
  function markPick(form, lang) {
    document.cookie = CHOSEN;
    if (!form) return;
    /* Every return_to: the header form carries two (Shopify's form tag adds one,
       the snippet another) and Shopify reads the last (live, 29 Sep 2026). */
    var fields = [].slice.call(form.querySelectorAll('input[name="return_to"]'));
    if (!fields.length) {
      var rt = document.createElement('input');
      rt.type = 'hidden'; rt.name = 'return_to'; rt.value = location.pathname;
      form.appendChild(rt);
      fields = [rt];
    }
    for (var i = 0; i < fields.length; i++) {
      var v = fields[i].value || location.pathname;
      if (!new RegExp('[?&]' + PICK + '=1').test(v)) v += (v.indexOf('?') > -1 ? '&' : '?') + PICK + '=1';
      /* A LANGUAGE picked by hand travels too: locale-auto-detect on the other
         domain remembers it there (cookies do not cross domains). */
      if (lang && /^[a-z]{2}(-[a-z]{2})?$/i.test(lang) && !/[?&]ss_lang=/.test(v)) v += '&ss_lang=' + String(lang).toLowerCase();
      fields[i].value = v;
    }
  }
  if (typeof document !== 'undefined') {
    window.SSMarketMarkPick = markPick;
    document.addEventListener('submit', function (e) {
      var f = e.target;
      if (f && f.id !== 'ss-mkt-form' && /\/localization$/.test(f.getAttribute('action') || '')) markPick(f);
    }, true);
  }

  if (typeof document === 'undefined') {
    globalThis.SSMarketRoute = { decide: decide, pickLanguage: pickLanguage, COPY: COPY, BOT: BOT, NO_ROUTE_PATH: NO_ROUTE_PATH, markPick: markPick };
    return;
  }
  run();
})();
